import {
  type RowData,
  type Table,
  type Row,
  type RowModel,
  assignPrototypeAPIs,
  type TableFeature,
  assignTableAPIs,
  type Cell,
  type CellData,
  type Header,
  type Column,
} from '@tanstack/react-table';

import { downloadCSV, escapeForCSV } from '../../../../core/utils/download.js';
import { devLog } from '../../../../core/utils/logging.js';
import {
  coerceDatetimeCellValue,
  coerceLongCellValue,
  coerceNumericCellValue,
} from '../../../utils/coerce-cell-value.js';
import { convertArrayToCsv } from '../../../utils/download-utils.js';
import {
  DATA_TABLE_INDEX_COLUMN_HEADER,
  DATA_TABLE_DOWNLOAD_CSV_FILE_NAME,
} from '../../constants.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import {
  createPrototypeConstructor,
  createTableConstructor,
} from '../types/table.types.js';
import type {
  DataTableDownloadHeaderContext,
  DataTableDownloadSlotProps,
} from './download-types.js';

/**
 * DataTable slot definition for the Download action.
 * Columns may be optionally excluded from downloading by their IDs.
 * @public
 */
export function DataTableDownload(props: DataTableDownloadSlotProps) {
  return null;
}

/**
 * Selects the leaf headers that take part in the CSV download: visible,
 * non-placeholder, non-builtin, non-sparkline, and not excluded by the consumer.
 *
 * Header groups are ordered by column pinning, so this list also defines the
 * column order of the exported rows.
 *
 * @param table - the table instance whose leaf headers are extracted.
 * @param excludeColumns - array of column IDs that can optionally be excluded from the download.
 */
function getDownloadLeafHeaders<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  excludeColumns?: string[],
): Header<TDataTableFeatures, TData>[] {
  return table.getLeafHeaders().filter((header) => {
    // Skip subheaders, we work through the remaining leaves later.
    if (header.subHeaders.length > 0) {
      return false;
    }

    // Don't include placeholder headers.
    if (header.isPlaceholder) {
      return false;
    }
    // Don't include built in columns in the download info.
    if (header.column.columnDef.meta?.builtin === true) {
      return false;
    }
    // Don't include columns explicitly excluded by the consumer in the download info.
    if (excludeColumns?.includes(header.column.id)) {
      return false;
    }
    // Don't include sparkline column in the download info.
    if (header.column.columnDef.columnType === 'sparkline') {
      return false;
    }
    if (header.column.getIsVisible() === false) {
      return false;
    }
    return true;
  });
}

/**
 * Extracts the column string representation for the download CSV.
 *
 * @param table - the table instance providing the default header formatter.
 * @param downloadHeaders - the leaf headers taking part in the download.
 * @param includeIndexColumnHeader - if true, prepends an `index` column,
 * indicating the row's indentation, e.g. 1, 1.1, 1.2, 2, 2.1., ...
 * @param formatDownloadHeader - optional callback to override CSV header names per column.
 */
function getCsvHeaders<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  downloadHeaders: Header<TDataTableFeatures, TData>[],
  includeIndexColumnHeader: boolean,
  formatDownloadHeader?: (
    context: DataTableDownloadHeaderContext,
  ) => string | undefined,
): string[] {
  const effectiveFormatDownloadHeader =
    formatDownloadHeader ?? table.options.formatDownloadHeader;

  const columnHeaders = downloadHeaders.map((header) => {
    const headerPath = header_getDownloadHeaderPath(header);
    const defaultHeader = headerPath.join('.');
    const overriddenHeader = effectiveFormatDownloadHeader?.({
      columnId: header.column.id,
      leafHeader: headerPath.at(-1) ?? defaultHeader,
      groupHeader: headerPath.at(-2),
    });
    return escapeForCSV(overriddenHeader ?? defaultHeader);
  });

  if (includeIndexColumnHeader) {
    columnHeaders.unshift(DATA_TABLE_INDEX_COLUMN_HEADER);
  }
  return columnHeaders;
}

/**
 * @returns the given row's values for the download columns, as downloadable strings.
 *
 * Values are read from column accessors rather than `row.getVisibleCells()`: that
 * method builds a cell object for every leaf column — hidden ones included — and
 * keeps them on the row, causing memory overhead.
 */
function getCsvCells<TData extends RowData>(
  row: Row<TDataTableFeatures, TData>,
  downloadColumns: Column<TDataTableFeatures, TData>[],
): string[] {
  const values: string[] = [];
  for (const column of downloadColumns) {
    values.push(
      getDownloadString(column.accessorFn?.(row.original, row.index), column),
    );
  }
  return values;
}

/**
 * @returns the given row's 1-based index, indicating its indentation level.
 * - Root rows have indices 1, 2, 3, 4, ...
 * - 2nd level sub-rows rows have indices 1.1, 1.2, 1.3, 2.1, 2.2, ...
 * - 3rd level sub-rows rows have indices 1.1.1, 1.1.2, 1.2.1, 1.3.1, 2.1.1, 2.1.2, ...
 * - etc.
 */
function getRowIndex<TData extends RowData>(
  row: Row<TDataTableFeatures, TData>,
) {
  let indexPrefix = '';
  for (const parent of row.getParentRows()) {
    indexPrefix = `${indexPrefix}${parent.index + 1}.`;
  }
  return `${indexPrefix}${row.index + 1}`;
}

/**
 * Extracts the headers and cell data string representation for the download CSV.
 * Utilizes async batching intended for large amounts of rows (e.g. 100k)
 * to unblock the main thread.
 *
 * If the table has sub-rows, an `index` column is prepended,
 * indicating the row's indentation, e.g. 1, 1.1, 1.2, 2, 2.1., ...
 *
 * @param table - the table instance whose leaf headers are extracted.
 * @param rowModel - the rows to process.
 * @param excludeColumns - array of column IDs that can optionally be excluded from the download.
 */
async function getCsv<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  rowModel: RowModel<TDataTableFeatures, TData>,
  excludeColumns?: string[],
  formatDownloadHeader?: (
    context: DataTableDownloadHeaderContext,
  ) => string | undefined,
): Promise<string[][]> {
  const nrOfRows = rowModel.flatRows.length;
  const csvData: string[][] = Array.from({ length: nrOfRows + 1 }); // rows + header
  const batchSize = 1_000;
  let currentIndex = 0;
  const includeIndexColumn = table.getHasSubRows();

  // Resolved once for the whole export: the same header list drives both the
  // header row and the per-row value order, so the two cannot drift apart.
  const downloadHeaders = getDownloadLeafHeaders(table, excludeColumns);
  const downloadColumns = downloadHeaders.map((header) => header.column);

  csvData[0] = getCsvHeaders(
    table,
    downloadHeaders,
    includeIndexColumn,
    formatDownloadHeader,
  );

  function processBatch(): Promise<void> {
    return new Promise((resolve) => {
      const currentBatchEnd = Math.min(currentIndex + batchSize, nrOfRows);
      for (let i = currentIndex; i < currentBatchEnd; i++) {
        const row = rowModel.flatRows[i];
        const csvRow = getCsvCells(row, downloadColumns);
        if (includeIndexColumn) {
          const rowIndex = getRowIndex(row);
          csvRow.unshift(rowIndex);
        }
        csvData[i + 1] = csvRow;
      }
      currentIndex = currentBatchEnd;

      if (currentIndex < nrOfRows) {
        // yield control to the browser before continuing with next batch
        setTimeout(() => resolve(processBatch()), 0);
      } else {
        resolve();
      }
    });
  }

  await processBatch();
  return csvData;
}

/**
 * Sanitizes a consumer-provided file name so it cannot crash the download or
 * escape the downloads folder. The `.csv` extension is stripped, only letters,
 * digits, dashes, and underscores are kept (all other characters are removed),
 * and the extension is re-added by the caller.
 * @returns the sanitized base name, or `undefined` when nothing usable remains.
 */
function sanitizeFileName(name: string | undefined): string | undefined {
  if (name === undefined) {
    return undefined;
  }
  const sanitized = name.replace(/\.csv$/i, '').replace(/[^a-zA-Z0-9_-]/g, '');
  return sanitized === '' ? undefined : sanitized;
}

/**
 * Resolves the effective CSV download file name: the given name is sanitized of
 * invalid characters and falls back to the default when nothing usable remains.
 * The `.csv` extension is always appended.
 */
function resolveDownloadFileName(fileName?: string): string {
  const sanitized = sanitizeFileName(fileName);
  return sanitized === undefined
    ? DATA_TABLE_DOWNLOAD_CSV_FILE_NAME
    : `${sanitized}.csv`;
}

function downloadData<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  rowModel: RowModel<TDataTableFeatures, TData>,
  subset: 'all' | 'page' | 'selected',
  excludeColumns?: string[],
  formatDownloadHeader?: (
    context: DataTableDownloadHeaderContext,
  ) => string | undefined,
  fileName?: string | ((subset: 'all' | 'page' | 'selected') => string),
  onDownloadEnd?: () => void,
): void {
  const resolvedName =
    typeof fileName === 'function' ? fileName(subset) : fileName;
  void getCsv(table, rowModel, excludeColumns, formatDownloadHeader)
    .then((rows) => {
      const csvString = convertArrayToCsv(rows);
      downloadCSV(csvString, resolveDownloadFileName(resolvedName));
      table.options.onDownloadData?.(
        subset,
        ...(excludeColumns ? [excludeColumns] : []),
      );
    })
    .catch((error: unknown) => {
      devLog('[DataTable] CSV download failed', error);
    })
    .finally(() => {
      onDownloadEnd?.();
    });
}

/**
 * Helper function to extract the header nesting segments for a leaf column, from outermost group to leaf.
 * Falls back to the header id when the column header is a custom renderer or undefined.
 * @internal
 */
export function header_getDownloadHeaderPath<TData extends RowData>(
  header: Header<TDataTableFeatures, TData>,
): string[] {
  const nestedHeaderNames: string[] = [];

  let currentColumnNesting: Column<TDataTableFeatures, TData> | undefined =
    header.column;

  // Go up the parent chain and find the correct header names for the download.
  while (currentColumnNesting !== undefined) {
    const columnDefHeader = currentColumnNesting.columnDef.header;
    // Fall back to the column id when the header is a custom renderer or undefined.
    if (
      typeof columnDefHeader === 'function' ||
      columnDefHeader === undefined
    ) {
      nestedHeaderNames.push(currentColumnNesting.id);
    } else {
      nestedHeaderNames.push(columnDefHeader);
    }
    // Go up the nesting chain.
    currentColumnNesting = currentColumnNesting.parent;
  }

  // Reverse so the path reads outermost group → leaf.
  return nestedHeaderNames.toReversed();
}

export function header_getDownloadString<TData extends RowData>(
  header: Header<TDataTableFeatures, TData>,
): string {
  return header_getDownloadHeaderPath(header).join('.');
}

/**
 * Converts a column value to its CSV string representation, with special handling
 * for dates, meter bars, and objects.
 *
 * Takes the value and its column rather than a cell so the caller can read values
 * through column accessors without building a cell object per value.
 * @internal
 */
export function getDownloadString<
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  cellValue: TValue,
  column: Column<TDataTableFeatures, TData, TValue>,
): string {
  if (cellValue === undefined || cellValue === null) {
    return '';
  }

  if (
    (column.columnDef.columnType === 'datetime' ||
      // DEPRECATION: [APPDEV-16044] BREAKING-CHANGE: Remove 'date' columnType.
      column.columnDef.columnType === 'date') &&
    (typeof cellValue === 'string' ||
      typeof cellValue === 'number' ||
      cellValue instanceof Date)
  ) {
    const cellDate = coerceDatetimeCellValue(cellValue);
    return cellDate !== null ? cellDate.toISOString() : '';
  }

  if (cellValue instanceof Date) {
    const cellDate = coerceDatetimeCellValue(cellValue);
    return cellDate !== null ? cellDate.toISOString() : '';
  }

  if (column.columnDef.columnType === 'long') {
    const coerced = coerceLongCellValue(cellValue);
    return coerced !== null ? escapeForCSV(`${coerced}`) : '';
  }

  if (
    column.columnDef.columnType === 'number' ||
    column.columnDef.columnType === 'bit' ||
    column.columnDef.columnType === 'currency'
  ) {
    const coerced = coerceNumericCellValue(cellValue);
    return coerced !== null ? escapeForCSV(`${coerced}`) : '';
  }

  if (column.columnDef.columnType === 'meterbar') {
    // Single `MeterbarChart` value, return the escaped value.
    if (!Array.isArray(cellValue)) {
      return escapeForCSV(cellValue);
    }
    // `MultiMeterBarChart` value, return the escaped JSON string of the name-value pairs.
    try {
      const multiMeterBarValues = Object.fromEntries(
        cellValue.map((item) => [item.name, item.value]),
      );
      return escapeForCSV(JSON.stringify(multiMeterBarValues));
    } catch {
      devLog(
        `Failed to extract meterbar values for column "${column.id}". Expected array items to have 'name' and 'value' properties.`,
      );
    }
  }

  if (typeof cellValue === 'string' || Array.isArray(cellValue)) {
    return escapeForCSV(cellValue);
  }

  if (typeof cellValue === 'object' && cellValue !== null) {
    // Prefer a custom toString over JSON.stringify when the object defines one.
    if (
      Object.hasOwn(cellValue, 'toString') &&
      typeof cellValue.toString === 'function'
    ) {
      return escapeForCSV(cellValue.toString());
    }

    try {
      return escapeForCSV(JSON.stringify(cellValue));
    } catch {
      devLog(
        `Accessed cell value for column "${column.id}" received an object, but was not stringifyable. Consider providing a toString() function on the object.`,
      );
    }
  }

  return `${cellValue}`;
}

/**
 * Converts a cell's value to its CSV string representation with special handling for dates, meter bars, and objects.
 * @internal
 */
export function cell_getDownloadString<
  TData extends RowData,
  TValue extends CellData = CellData,
>(cell: Cell<TDataTableFeatures, TData, TValue>): string {
  return getDownloadString(cell.getValue(), cell.column);
}

/**
 * Triggers a CSV download of all table rows, excluding built-in and sparkline columns.
 * @internal
 */
export function table_downloadAllData<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  excludeColumns?: string[],
  formatDownloadHeader?: (
    context: DataTableDownloadHeaderContext,
  ) => string | undefined,
  fileName?: string | ((subset: 'all' | 'page' | 'selected') => string),
  onDownloadEnd?: () => void,
) {
  return downloadData(
    table,
    table.getCoreRowModel(),
    'all',
    excludeColumns,
    formatDownloadHeader,
    fileName,
    onDownloadEnd,
  );
}

/**
 * Triggers a CSV download of the current page's rows, excluding built-in and sparkline columns.
 * @internal
 */
export function table_downloadPageData<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  excludeColumns?: string[],
  formatDownloadHeader?: (
    context: DataTableDownloadHeaderContext,
  ) => string | undefined,
  fileName?: string | ((subset: 'all' | 'page' | 'selected') => string),
  onDownloadEnd?: () => void,
) {
  return downloadData(
    table,
    table.getPaginatedRowModel(),
    'page',
    excludeColumns,
    formatDownloadHeader,
    fileName,
    onDownloadEnd,
  );
}

/**
 * Triggers a CSV download of the currently selected rows, excluding built-in and sparkline columns.
 * @internal
 */
export function table_downloadSelectedData<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  excludeColumns?: string[],
  formatDownloadHeader?: (
    context: DataTableDownloadHeaderContext,
  ) => string | undefined,
  fileName?: string | ((subset: 'all' | 'page' | 'selected') => string),
  onDownloadEnd?: () => void,
) {
  return downloadData(
    table,
    table.getSelectedRowModel(),
    'selected',
    excludeColumns,
    formatDownloadHeader,
    fileName,
    onDownloadEnd,
  );
}

/**
 * Constructs the download feature for the table, including API implementations and prototype assignments.
 * @internal
 */
export function constructDownloadDataFeature(): TableFeature {
  return {
    assignHeaderPrototype: createPrototypeConstructor(
      (
        prototype: Record<string, unknown>,
        table: Table<TDataTableFeatures, RowData>,
      ) => {
        assignPrototypeAPIs('DataTableDownloadDataFeature', prototype, table, {
          header_getDownloadString: {
            fn: (header) => header_getDownloadString(header),
          },
        });
      },
    ),
    assignCellPrototype: createPrototypeConstructor(
      (
        prototype: Record<string, unknown>,
        table: Table<TDataTableFeatures, RowData>,
      ) => {
        assignPrototypeAPIs('DataTableDownloadDataFeature', prototype, table, {
          cell_getDownloadString: {
            fn: (cell) => cell_getDownloadString(cell),
          },
        });
      },
    ),
    constructTableAPIs: createTableConstructor(
      (table: Table<TDataTableFeatures, RowData>) => {
        assignTableAPIs('DataTableDownloadDataFeature', table, {
          table_downloadAllData: {
            fn: (
              excludeColumns?: string[],
              formatDownloadHeader?: (
                context: DataTableDownloadHeaderContext,
              ) => string | undefined,
              fileName?:
                | string
                | ((subset: 'all' | 'page' | 'selected') => string),
              onDownloadEnd?: () => void,
            ) =>
              table_downloadAllData(
                table,
                excludeColumns,
                formatDownloadHeader,
                fileName,
                onDownloadEnd,
              ),
          },
          table_downloadPageData: {
            fn: (
              excludeColumns?: string[],
              formatDownloadHeader?: (
                context: DataTableDownloadHeaderContext,
              ) => string | undefined,
              fileName?:
                | string
                | ((subset: 'all' | 'page' | 'selected') => string),
              onDownloadEnd?: () => void,
            ) =>
              table_downloadPageData(
                table,
                excludeColumns,
                formatDownloadHeader,
                fileName,
                onDownloadEnd,
              ),
          },
          table_downloadSelectedData: {
            fn: (
              excludeColumns?: string[],
              formatDownloadHeader?: (
                context: DataTableDownloadHeaderContext,
              ) => string | undefined,
              fileName?:
                | string
                | ((subset: 'all' | 'page' | 'selected') => string),
              onDownloadEnd?: () => void,
            ) =>
              table_downloadSelectedData(
                table,
                excludeColumns,
                formatDownloadHeader,
                fileName,
                onDownloadEnd,
              ),
          },
        });
      },
    ),
  };
}

/**
 * TanStack Table feature that provides CSV download functionality.
 * @internal
 */
export const DataTableDownloadDataFeature = constructDownloadDataFeature();
